import React, { Component } from 'react';
import { Table, Tag, Spin } from 'antd';
import {
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { withTranslation } from 'react-i18next';
import './Output.scss';
import 'font-awesome/css/font-awesome.min.css';
import StatusBox from '../UI/StatusBox/StatusBox';
import { LogoStatus } from '../../const';

const { NotAttempted, Accepted, Unknown, TimeLimit, WrongAnswer, CompilerError, RuntimeError } =
  LogoStatus;

const LogoStyleMap = {
  [NotAttempted]: 'gray',
  [Unknown]: 'gray',
  [Accepted]: ' green',
  LOAD: 'gray',
  [TimeLimit]: 'orange',
  [WrongAnswer]: 'red',
  [CompilerError]: 'orange',
  [RuntimeError]: 'red',
};

const LogoVerdictMap = {
  [NotAttempted]: '',
  [Unknown]: ' Not Attempted',
  [Accepted]: ' Success',
  LOAD: '',
  [TimeLimit]: ' Time Limit Exceeded',
  [WrongAnswer]: ' Failed',
  [CompilerError]: ' Compilation Failed',
  [RuntimeError]: ' Runtime Error',
};

const LogoAntdMap = {
  [NotAttempted]: <QuestionCircleOutlined />,
  [Unknown]: <ExclamationCircleOutlined />,
  [Accepted]: <CheckCircleOutlined />,
  LOAD: <LoadingOutlined spin />,
  [TimeLimit]: <ClockCircleOutlined />,
  [WrongAnswer]: <CloseCircleOutlined />,
  [CompilerError]: <CodeOutlined />,
  [RuntimeError]: <WarningOutlined />,
};

const difficultyColorMap = {
  EZ: '#4E957F',
  MD: '#ffb933',
  HD: '#D63321',
};

class OutPut extends Component {
  render() {
    const { t } = this.props;

    let index = -1;
    const data = this.props.testcases.map(function (data) {
      index++;
      return { index, key: index, ...data };
    });

    const difficultyTextMap = {
      EZ: t('output.difficulty.easy'),
      MD: t('output.difficulty.medium'),
      HD: t('output.difficulty.hard'),
    };

    const columns = [
      {
        title: t('output.table.sample_case'),
        dataIndex: 'index',
        key: 'index',
      },
      {
        title: t('output.table.difficulty_level'),
        dataIndex: 'difficulty',
        key: 'difficulty',
        render: data => (
          <Tag
            style={{
              color: difficultyColorMap[data],
              backgroundColor: 'transparent',
              borderBlockColor: difficultyColorMap[data],
              border: '1px solid',
            }}
            color={difficultyColorMap[data]}
          >
            {difficultyTextMap[data]}
          </Tag>
        ),
      },
      {
        title: t('output.table.points'),
        dataIndex: 'points',
        key: 'points',
      },
      {
        title: t('output.table.status'),
        dataIndex: 'logo',
        key: 'status',
        render: logo => (
          <>
            <span style={{ color: LogoStyleMap[logo] }}>{LogoAntdMap[logo]}</span>
            {LogoVerdictMap[logo]}
          </>
        ),
      },
    ];

    const passCases = this.props.testcases.filter(tc => tc.logo === Accepted).length;
    const totalCases = this.props.testcases.length;
    const leftCases = totalCases - passCases;

    let message = t('output.messages.partial_fail', {
      left: leftCases,
      total: totalCases,
    });

    if (this.props.probStatus === 'ALL_PASS') {
      if (this.props.leftCount > 0) {
        message = t('output.messages.all_pass_some_left', {
          count: this.props.leftCount,
        });
      } else {
        message = t('output.messages.all_pass_completed');
      }
    } else if (this.props.probStatus === NotAttempted) {
      message = t('output.messages.not_attempted');
    }

    if (this.props.isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
          }}
        >
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#666' }}>{t('output.messages.running_tests')}</p>
        </div>
      );
    }

    const allTestsPassed = passCases === totalCases && totalCases > 0;

    const testcaseError = this.props.testcases.find(tc => tc.compiler_error || tc.runtime_error);

    return (
      <div data-testid="code-output">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '16px',
            fontSize: '14px',
            color: '#000',
          }}
        >
          {!allTestsPassed ? (
            <p style={{ paddingTop: '15px' }}>
              <em data-testid="code-output-status">{message}</em>
            </p>
          ) : (
            <>
              <span data-testid="code-output-success">{t('output.messages.all_tests_passed')}</span>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            </>
          )}
        </div>

        <h2
          style={{
            fontWeight: 'bold',
            textAlign: 'left',
            paddingLeft: '20px',
            marginBottom: '20px',
          }}
        >
          {t('output.header.output')}
        </h2>

        {testcaseError && (
          <StatusBox
            title={`${t('output.status_box.status_prefix')} ${
              testcaseError.compiler_error
                ? t('output.status_box.compilation_failed')
                : t('output.status_box.runtime_error')
            }`}
            value={testcaseError.stderr}
            readOnly={true}
            error={true}
            data-testid="code-output-error"
          />
        )}

        <Table
          data-testid="code-output-table"
          columns={columns}
          dataSource={data}
          pagination={false}
          rowClassName={() => ''}
        />
      </div>
    );
  }
}

export default withTranslation('interview_problem')(OutPut);
